// `search.page.tpl.html:637` — the criteria builder's fifth section, and the biggest.
//
// ⚠️ ITS SHAPE CHANGES WITH THE COUNTRY, IN TWO SEPARATE WAYS, and neither is a simple
// enable/disable:
//
//   - the REGION checkboxes come from `careero.extensionRegionDetailsByCountry[country]` and
//     are split into two columns by `showOnMetroList` — the metro column is headed
//     "Metropolitan Area" and is absent when the country has none. A country with no regions
//     at all drops the block entirely, including the `area-section` class its wrapper only
//     carries when the non-metro list is non-empty.
//   - the CITY field is an `ng-switch` on the country: Israel gets "The following cities"
//     against `cities` (300px, city autocomplete), and everything else gets "The following
//     cities / regions" against `globalCitiesOrRegions` (400px, a different endpoint and a
//     different display property). They are different criteria keys, not one field with two
//     labels, so a country change has to clear the other.
//
// And the country change itself is destructive on purpose: `onLocationCriteriaCountryChanged`
// rebuilds `location` from scratch, keeping ONLY `country`, `allowUnknown` and `distance`.
// Every city, region and include-flag is dropped, because they name places that no longer
// exist in the new country.
import type { ReactElement } from 'react'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { LocationSuggestionPanel } from './LocationSuggestionPanel.tsx'
import { tagsPlaceholder } from './SkillSetSection.tsx'
import { LOCATION_COUNTRIES, LOCATION_COUNTRY_PINNED, LOCATION_DISTANCES } from './criteriaOptions.ts'

/** `IExtensionRegionDetailsDTO` — one region checkbox. */
export interface LocationRegion {
  name: string
  displayText: string
  /**
   * ⚠️ CARRIED FOR THE SAVE, NOT FOR THE RENDER. `mapLocationCriteria` looks the picked region
   * name up in this list and sends the FULL object — `{ id, region_state, country }` — so a
   * region list narrowed to what the checkboxes need cannot round-trip a selection.
   */
  id?: number
  country?: string
  /** Puts it in the second column, under "Metropolitan Area". */
  showOnMetroList?: boolean
  /** Rendered as an info icon beside the label; the tooltip body is HTML upstream. */
  tooltipHtml?: string | null
}

/** `ITempExtensionCriteria['location']`. */
export interface LocationCriteria {
  country?: string
  includeDistanceFrom?: boolean
  distance?: number
  distanceCities?: Tag[]
  regions?: Record<string, boolean>
  /** Israel only. */
  includeCities?: boolean
  cities?: Tag[]
  /** Everywhere else. */
  includeGlobalCitiesOrRegions?: boolean
  globalCitiesOrRegions?: Tag[]
  allowUnknown?: boolean
}

export interface LocationSectionProps {
  /** `typeaheads.getCities($query, country, false, false, true)` — `allowNullCountry`. */
  loadDistanceCities?: (query: string) => Promise<readonly Tag[]>
  /** `typeaheads.getCities($query, country, false, false)` — needs a country. */
  loadCities?: (query: string) => Promise<readonly Tag[]>
  /** `typeaheads.getLocationsOrRegionsFull($query, country, false)`. */
  loadGlobalLocations?: (query: string) => Promise<readonly Tag[]>
  criteria: LocationCriteria
  /** `extensionRegionDetailsByCountry[country]` — already narrowed to the current country. */
  regions?: readonly LocationRegion[]
  citySuggestions?: readonly Tag[]
  /**
   * The noun in "Show … with unknown location", which is the ONE line of this section's copy
   * that differs between the two builders: `search.page.tpl.html:866` says candidates,
   * `client-contact-search.page.tpl.html:654` says contacts and its company-location twin at
   * :893 says companies. Ported shared, the contact form said "candidates" about a list of
   * people it never shows.
   */
  subject?: 'candidates' | 'contacts' | 'companies'
  onCityQueryChange?: (query: string) => void
  onChange?: (criteria: LocationCriteria) => void
}

/** `$window.careero.extensionRegionDetailsByCountry` keyed by the country the select holds. */
export function regionsForCountry(
  byCountry: Readonly<Record<string, readonly LocationRegion[]>> | undefined,
  country: string | undefined,
): readonly LocationRegion[] {
  if (byCountry === undefined || country === undefined || country === '') return []
  return byCountry[country] ?? []
}

/**
 * `onLocationCriteriaCountryChanged` — everything but these three is dropped.
 *
 * Exported and tested because it is the one piece of this section that silently keeps stale
 * data if transcribed as a spread.
 */
export function locationAfterCountryChange(previous: LocationCriteria, country: string): LocationCriteria {
  return {
    country,
    ...(previous.allowUnknown === undefined ? {} : { allowUnknown: previous.allowUnknown }),
    ...(previous.distance === undefined ? {} : { distance: previous.distance }),
  }
}

function RegionColumn({
  regions,
  checked,
  onToggle,
  heading,
}: {
  regions: readonly LocationRegion[]
  checked: Record<string, boolean>
  onToggle: (name: string, value: boolean) => void
  heading?: string
}): ReactElement {
  return (
    <div className="area-regions-col">
      {heading === undefined ? null : (
        <div className="mB7">
          <strong>{heading}</strong>
        </div>
      )}
      {regions.map((region) => (
        <div key={region.name}>
          <WooCheckbox checked={checked[region.name] === true} onChange={(value) => onToggle(region.name, value)}>
            {region.displayText}
          </WooCheckbox>
          {/* ⚠️ `null`, NOT JUST `undefined`. The tenant sends `tooltipHtml: null` for every
              region that has no tooltip — which is most of them — and a guard that checks only
              `undefined` lets it through to `.replace`, throwing inside render and taking the
              whole frame down with "Bootstrap failed" the moment a country is chosen. Same
              shape as `creationDate`: the DTO's optional marker is not the wire's null. */}
          {region.tooltipHtml === undefined || region.tooltipHtml === null || region.tooltipHtml === '' ? null : (
            <span>
              {/* `shortlist-tooltip` is a directive of upstream's; the icon is what shows at
                  rest and the tooltip arrives with that directive. */}
              <i className="cursor-pointer fa fa-info-circle" title={region.tooltipHtml.replace(/<[^>]*>/g, '')} />
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

export function LocationSection({
  criteria,
  regions = [],
  citySuggestions = [],
  subject = 'candidates',
  onCityQueryChange,
  onChange,
  loadDistanceCities,
  loadCities,
  loadGlobalLocations,
}: LocationSectionProps): ReactElement {
  const patch = (next: Partial<LocationCriteria>): void => onChange?.({ ...criteria, ...next })
  const metro = regions.filter((region) => region.showOnMetroList === true)
  const nonMetro = regions.filter((region) => region.showOnMetroList !== true)
  const checkedRegions = criteria.regions ?? {}
  const isIsrael = criteria.country === 'Israel'

  const toggleRegion = (name: string, value: boolean): void =>
    patch({ regions: { ...checkedRegions, [name]: value } })

  return (
    // ⚠️ THE OPTIONS BLOCK IS A SIBLING OF `.location-criteria`, NOT A CHILD. Both sit
    // directly inside `.location-wrap`, which the shell supplies. Nesting it — which the
    // indentation of the template invites, since it closes three divs in a row just above —
    // puts a 54px block inside a container the cascade sizes from its own content.
    <>
      <div className="location-criteria">
        <div className="form-group sub-section">
          <label className="bold-soft">Country</label>
          {/* The inline width is upstream's own, on the element rather than in the stylesheet. */}
          <div className="select mB15" style={{ width: '200px' }}>
            <select
              className="form-control input"
              value={criteria.country ?? ''}
              onChange={(event) => onChange?.(locationAfterCountryChange(criteria, event.target.value))}
            >
              <option value="">Worldwide</option>
              {/* ⚠️ THE SEPARATORS ARE REAL OPTIONS. A disabled `──────────` occupies a row in the
                open dropdown; replacing them with `<optgroup>`, which is the tidier markup,
                changes the list's height and indents every entry. */}
              <option disabled>──────────</option>
              {LOCATION_COUNTRY_PINNED.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
              <option disabled>──────────</option>
              {LOCATION_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <label className="bold-soft">Area</label>
          <div>
            <div className="location-criteria-sections area-section">
              <div className="location-cities">
                <WooCheckbox
                  checked={criteria.includeDistanceFrom === true}
                  onChange={(includeDistanceFrom) => patch({ includeDistanceFrom })}
                >
                  Up to
                </WooCheckbox>
                <div className="select mL15">
                  <select
                    className="form-control input"
                    value={criteria.distance ?? ''}
                    onChange={(event) => patch({ distance: Number(event.target.value) })}
                  >
                    {/* ⚠️ ANGULAR'S "UNKNOWN OPTION", AND WITHOUT IT THE FIRST RADIUS LOOKS
                        CHOSEN. `ng-options` prepends `<option value="?">` whenever the model
                        matches no option, so production shows the select BLANK until someone
                        picks a distance; a React `<select>` with no matching value selects its
                        first child instead, which put "10km (6 miles)" on screen — and on a
                        form where the distance checkbox is unticked, that reads as a filter
                        nobody set. Measured against build 354 on the contact form's location
                        section, where the reference shows an empty select. */}
                    {criteria.distance === undefined ? <option value="" /> : null}
                    {LOCATION_DISTANCES.map((option) => (
                      <option key={option.distance} value={option.distance}>
                        {option.display}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="mL15">from</label>
                <div style={{ width: '200px' }} className="mL15">
                  {/* ⚠️ PLACEHOLDER "City", NOT the multi-tag helper the other fields use — this
                    one is a fixed string even once it has tags. */}
                  <TagsInput
                    name="distance-from-cities"
                    className="or-in-between"
                    placeholder="City"
                    tags={criteria.distanceCities ?? []}
                    indicator
                    indicatorClassName="fas fa-search"
                    suggestions={citySuggestions}
                    {...(loadDistanceCities === undefined ? {} : { loadSuggestions: loadDistanceCities })}
                    renderSuggestions={LocationSuggestionPanel}
                    onQueryChange={onCityQueryChange}
                    // `onLocationCriteriaDistanceCitiesChanged` sets the include flag FROM the
                    // list's length — ticking the box is not what turns the criterion on.
                    onAdd={(tag) => {
                      const next = [...(criteria.distanceCities ?? []), tag]
                      patch({ distanceCities: next, includeDistanceFrom: next.length > 0 })
                    }}
                    onRemove={(at) => {
                      const next = (criteria.distanceCities ?? []).filter((_, i) => i !== at)
                      patch({ distanceCities: next, includeDistanceFrom: next.length > 0 })
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ⚠️ THE WRAPPER IS UNCONDITIONAL; ONLY THE METRO COLUMN HAS AN `ng-if`. A country
              with no regions still renders this div, at zero height — it is a flex container
              with an empty column. Gating the whole block on `regions.length` is the tidier
              reading and removes an element the cascade counts, which on a Worldwide search is
              the difference between matching production and not. `area-section` is the part
              that IS conditional, on the non-metro list being non-empty. */}
            <div className={`location-criteria-sections area-regions${nonMetro.length > 0 ? ' area-section' : ''}`}>
              <RegionColumn regions={nonMetro} checked={checkedRegions} onToggle={toggleRegion} />
              {metro.length === 0 ? null : (
                <RegionColumn
                  regions={metro}
                  checked={checkedRegions}
                  onToggle={toggleRegion}
                  heading="Metropolitan Area"
                />
              )}
            </div>

            <div className="area-section">
              <div>
                <div className="location-criteria-sections">
                  <div className="location-cities">
                    {isIsrael ? (
                      <>
                        <WooCheckbox
                          checked={criteria.includeCities === true}
                          onChange={(includeCities) => patch({ includeCities })}
                        >
                          The following cities
                        </WooCheckbox>
                        <div style={{ width: '300px' }} className="mL15">
                          <TagsInput
                            name="cities"
                            className="or-in-between"
                            placeholder={tagsPlaceholder(criteria.cities, 'city')}
                            tags={criteria.cities ?? []}
                            indicator
                            indicatorClassName="fas fa-search"
                            suggestions={citySuggestions}
                            {...(loadCities === undefined ? {} : { loadSuggestions: loadCities })}
                            renderSuggestions={LocationSuggestionPanel}
                            onQueryChange={onCityQueryChange}
                            onAdd={(tag) => {
                              const next = [...(criteria.cities ?? []), tag]
                              patch({ cities: next, includeCities: next.length > 0 })
                            }}
                            onRemove={(at) => {
                              const next = (criteria.cities ?? []).filter((_, i) => i !== at)
                              patch({ cities: next, includeCities: next.length > 0 })
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <WooCheckbox
                          checked={criteria.includeGlobalCitiesOrRegions === true}
                          onChange={(includeGlobalCitiesOrRegions) => patch({ includeGlobalCitiesOrRegions })}
                        >
                          The following cities / regions
                        </WooCheckbox>
                        <div style={{ width: '400px' }} className="mL15">
                          <TagsInput
                            name="cities"
                            className="or-in-between"
                            placeholder={tagsPlaceholder(criteria.globalCitiesOrRegions, 'region / state / city')}
                            tags={criteria.globalCitiesOrRegions ?? []}
                            // ⚠️ THE ONE FIELD WITH ITS OWN TAG TEMPLATE. `search.page.tpl.html:837`
                            // gives this list `location-tags-input.tpl.html`, which composes
                            // "city, region, country" instead of printing a display property;
                            // the city lists on either side of it use the generic template with
                            // `display-property="city"`. Both contact-builder location lists are
                            // this field too (`client-contact-search.page.tpl.html:625` and :864).
                            tagTemplate="location"
                            indicator
                            indicatorClassName="fas fa-search"
                            suggestions={citySuggestions}
                            {...(loadGlobalLocations === undefined ? {} : { loadSuggestions: loadGlobalLocations })}
                            renderSuggestions={LocationSuggestionPanel}
                            onQueryChange={onCityQueryChange}
                            onAdd={(tag) => {
                              const next = [...(criteria.globalCitiesOrRegions ?? []), tag]
                              patch({ globalCitiesOrRegions: next, includeGlobalCitiesOrRegions: next.length > 0 })
                            }}
                            onRemove={(at) => {
                              const next = (criteria.globalCitiesOrRegions ?? []).filter((_, i) => i !== at)
                              patch({ globalCitiesOrRegions: next, includeGlobalCitiesOrRegions: next.length > 0 })
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sub-section">
        <div>
          <label className="bold-soft">Options</label>
        </div>
        <div>
          <WooCheckbox checked={criteria.allowUnknown === true} onChange={(allowUnknown) => patch({ allowUnknown })}>
            {/* The bold word is upstream's own `<b>`, inside the label. */}
            Show {subject} with <b>unknown location</b>
          </WooCheckbox>
        </div>
      </div>
    </>
  )
}
